<template>
  <div class="ibox">
    <div class="ibox-title"><h5>权限开通订单</h5></div>
    <div class="ibox-content">
      <div class="summary-row">
        <div class="summary-card">
          <div class="summary-label">今日已支付</div>
          <div class="summary-value">{{ summary.today.count }} 笔 / ￥{{ formatAmount(summary.today.amount) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">本月已支付</div>
          <div class="summary-value">{{ summary.month.count }} 笔 / ￥{{ formatAmount(summary.month.amount) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">近 30 天权限 Top3</div>
          <div class="summary-value summary-top">
            <span v-for="row in summary.topPermissions" :key="row.permissionKey" class="summary-top-item">
              {{ row.permissionName || row.permissionKey }} {{ row.count }} 笔
            </span>
            <span v-if="!summary.topPermissions.length" class="summary-empty">暂无数据</span>
          </div>
        </div>
      </div>

      <div class="crud-bar">
        <el-input v-model="filters.userId" placeholder="用户 ID" clearable style="width:110px" />
        <el-input v-model="filters.phone" placeholder="手机号（模糊）" clearable style="width:150px" />
        <el-select v-model="filters.permissionKey" placeholder="权限项" clearable style="width:160px">
          <el-option v-for="item in permissionOptions" :key="item.key" :label="item.name" :value="item.key" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态" clearable style="width:120px">
          <el-option label="待支付" value="0" />
          <el-option label="已支付" value="1" />
          <el-option label="已取消" value="2" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width:260px" />
        <el-input v-model="filters.outTradeNo" placeholder="订单号（模糊）" clearable style="width:190px" />
        <el-button icon="el-icon-search" type="primary" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
        <el-button icon="el-icon-download" :loading="exporting" @click="exportCsv">导出对账</el-button>
      </div>

      <div v-if="truncated" class="truncate-tip">
        仅统计最近 5000 条明细，请缩小时间范围后再导出，避免漏账。
      </div>

      <el-table :data="list" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="out_trade_no" label="订单号" width="210" />
        <el-table-column label="用户" width="150">
          <template slot-scope="s">
            <div>{{ s.row.user_id || '-' }}</div>
            <div class="cell-sub">{{ s.row.phone || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="permission_name" label="权限项" width="130" />
        <el-table-column label="金额" width="100">
          <template slot-scope="s">
            <span class="amount">￥{{ formatAmount(s.row.price) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="s">
            <span class="label" :class="statusClass(s.row.status)">{{ statusText(s.row.status) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="pay_scene" label="场景" width="90" />
        <el-table-column prop="source" label="来源" width="170" />
        <el-table-column prop="transaction_id" label="微信交易号" width="200" />
        <el-table-column prop="created_at" label="创建时间" width="160" />
        <el-table-column prop="paid_at" label="支付时间" width="160" />
        <el-table-column prop="expire_at" label="到期时间" width="160" />
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="s">
            <el-button size="mini" @click="openDetail(s.row)">详情</el-button>
            <el-button
              v-if="s.row.status !== '1'"
              size="mini"
              type="warning"
              plain
              @click="manualApply(s.row)">补开</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page.sync="page"
        :page-size="pageSize"
        @current-change="fetch" />

      <el-dialog title="权限开通详情" :visible.sync="detailVisible" width="560px">
        <pre class="detail-pre">{{ detailText }}</pre>
        <div class="detail-tip">
          补开仅用于「微信回调丢失」的异常订单：只对该笔未支付明细补记已支付并开通权益，
          不会重复收款；如用户确实未付款，请勿使用。
        </div>
        <span slot="footer">
          <el-button
            v-if="detailRow && detailRow.status !== '1'"
            type="warning"
            @click="manualApply(detailRow)">手动补开</el-button>
          <el-button @click="detailVisible = false">关闭</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { permissionOrdersApi } from '../api/business'
import purchaseCatalog from '@t/utils/permissionPurchaseCatalog'

const EMPTY_SUMMARY = () => ({
  today: { count: 0, amount: 0 },
  month: { count: 0, amount: 0 },
  topPermissions: []
})

export default {
  name: 'AdminPermissionOrders',
  data () {
    return {
      loading: false,
      exporting: false,
      filters: {
        userId: '',
        phone: '',
        permissionKey: '',
        status: '',
        outTradeNo: ''
      },
      dateRange: [],
      list: [],
      total: 0,
      page: 1,
      pageSize: 50,
      truncated: false,
      summary: EMPTY_SUMMARY(),
      detailVisible: false,
      detailText: '',
      detailRow: null,
      permissionOptions: purchaseCatalog.getPurchaseCatalog()
    }
  },
  created () {
    this.fetch()
  },
  methods: {
    buildParams () {
      const [dateFrom = '', dateTo = ''] = this.dateRange || []
      return {
        userId: this.filters.userId,
        phone: this.filters.phone,
        permissionKey: this.filters.permissionKey,
        status: this.filters.status,
        outTradeNo: this.filters.outTradeNo,
        dateFrom,
        dateTo,
        page: this.page,
        pageSize: this.pageSize
      }
    },
    async fetch () {
      this.loading = true
      try {
        const data = await permissionOrdersApi.list(this.buildParams())
        this.list = (data && data.list) || []
        this.total = (data && data.total) || 0
        this.truncated = Boolean(data && data.truncated)
        this.summary = (data && data.summary) || EMPTY_SUMMARY()
      } catch (e) {
        this.$message.error(e.uiMessage || e.message)
      } finally {
        this.loading = false
      }
    },
    search () {
      this.page = 1
      this.fetch()
    },
    resetFilters () {
      this.filters = { userId: '', phone: '', permissionKey: '', status: '', outTradeNo: '' }
      this.dateRange = []
      this.search()
    },
    formatAmount (value) {
      const num = Number(value)
      return Number.isFinite(num) ? num.toFixed(2) : '0.00'
    },
    statusText (status) {
      return { 0: '待支付', 1: '已支付', 2: '已取消' }[status] || status || '-'
    },
    statusClass (status) {
      if (status === '1') return 'label-success'
      if (status === '2') return 'label-default'
      return 'label-warning'
    },
    openDetail (row) {
      this.detailRow = row
      this.detailText = JSON.stringify(row, null, 2)
      this.detailVisible = true
    },
    async manualApply (row) {
      if (!row) {
        return
      }
      const name = row.permission_name || row.permission_key || ''
      const confirmed = window.confirm(
        `确认对该订单手动补开「${name}」吗？\n\n仅用于微信回调丢失的异常订单；` +
        '补开不会重复收款，但若用户实际未付款请勿操作。'
      )
      if (!confirmed) {
        return
      }
      try {
        await permissionOrdersApi.manualApply(row.id, 'admin')
        this.$message.success('已补开，刷新后可见')
        this.detailVisible = false
        this.fetch()
      } catch (e) {
        this.$message.error(e.uiMessage || e.message)
      }
    },
    async exportCsv () {
      this.exporting = true
      try {
        const params = this.buildParams()
        delete params.page
        delete params.pageSize
        const blob = await permissionOrdersApi.export(params)
        const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.download = `permission-orders-${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error(e.uiMessage || e.message)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.summary-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f5f7fa;
}

.summary-label {
  font-size: 12px;
  color: #909399;
}

.summary-value {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.summary-top {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 14px;
}

.summary-top-item {
  color: #4a6fa5;
}

.summary-empty {
  font-size: 13px;
  color: #909399;
  font-weight: 400;
}

.crud-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.truncate-tip {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 12px;
}

.cell-sub {
  font-size: 12px;
  color: #909399;
}

.amount {
  color: #c79745;
  font-weight: 600;
}

.detail-pre {
  max-height: 320px;
  overflow: auto;
  padding: 12px;
  border-radius: 6px;
  background: #f5f7fa;
  font-size: 12px;
}

.detail-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.6;
}

.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
